// Chest / Rib Template Configuration
// Questions simplified to 12-year-old reading level

export const ribTemplate = {
  id: 'RIB',
  name: 'Chest / Rib',
  icon: '🫁',
  dominantSide: 'none',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Which side is the problem on?',
      options: ['Right', 'Left', 'Both', 'Centre / sternum'],
      required: true
    },

    ribLevel: {
      type: 'text',
      label: 'Can you point to the rib level? (if you know it)',
      placeholder: 'e.g., lower ribs, middle ribs, upper ribs'
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Gradually', 'After coughing'],
      required: true
    },

    injuryDate: {
      type: 'date',
      label: 'When did your chest or rib problem start?',
      required: true
    },

    mechanism: {
      type: 'checkbox-multiple',
      label: 'How did it happen? (tick all that apply)',
      options: [
        'Direct blow',
        'Fall',
        'Twisting',
        'Coughing',
        'Repetitive sport',
        'Heavy lifting',
        'Motor vehicle accident (MVA)',
        'Gradual — no specific event',
        'Other'
      ]
    },

    mechanismOther: {
      type: 'text',
      label: 'Please describe how it happened:',
      placeholder: 'Tell us what happened',
      showWhen: { field: 'mechanism', includes: 'Other' }
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Tell us what happened in your own words:',
      placeholder: 'Describe how your chest or rib problem started',
      rows: 3,
      required: true
    },

    sportSpecific: {
      type: 'checkbox-multiple',
      label: 'Do you play any of these sports? (tick all that apply)',
      options: [
        'Rowing',
        'Golf',
        'Throwing',
        'Swimming',
        'Bat-and-ball',
        'Weightlifting / CrossFit',
        'None'
      ]
    },

    recentTrainingChange: {
      type: 'radio',
      label: 'Have you recently changed how much or how hard you train?',
      options: ['Yes, I increased', 'No', 'Unsure']
    },

    // Pain Section
    painLocation: {
      type: 'text',
      label: 'Where exactly do you feel the pain?',
      placeholder: 'e.g., right side, lower ribs at the front'
    },

    painCharacter: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like? (tick all that apply)',
      options: ['Sharp', 'Aching', 'Stabbing', 'Bruised feeling', 'Other']
    },

    painCharacterOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painCharacter', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painWithBreathing: {
      type: 'radio',
      label: 'Does it hurt when you breathe?',
      options: ['Yes, with every breath', 'Only with deep breaths', 'No']
    },

    painWithCough: {
      type: 'radio',
      label: 'Does it hurt when you cough or sneeze?',
      options: ['Yes', 'No']
    },

    painWithRotation: {
      type: 'radio',
      label: 'Does it hurt when you twist your body?',
      options: ['Yes', 'No']
    },

    painAtNight: {
      type: 'radio',
      label: 'Does the pain wake you up at night?',
      options: ['Yes', 'No']
    },

    painWithLyingOnSide: {
      type: 'radio',
      label: 'Does it hurt when you lie on the sore side?',
      options: ['Yes', 'No']
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse? (tick all that apply)',
      options: [
        'Breathing deeply',
        'Coughing',
        'Laughing',
        'Bending',
        'Lifting',
        'Pushing',
        'Sport or exercise',
        'Lying on the sore side',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better? (tick all that apply)',
      options: [
        'Rest',
        'Shallow breathing',
        'Ice',
        'Heat',
        'Pain medication',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Red Flags (cardiac/pulmonary)
    shortnessOfBreath: {
      type: 'radio',
      label: 'Are you having trouble breathing or feeling short of breath?',
      options: ['Yes', 'No']
    },

    coughingBlood: {
      type: 'radio',
      label: 'Have you coughed up any blood?',
      options: ['Yes', 'No']
    },

    severeChestPain: {
      type: 'radio',
      label: 'Do you have severe chest pain that does not go away?',
      options: ['Yes', 'No']
    },

    cardiacRadiation: {
      type: 'radio',
      label: 'Does the pain spread to your jaw, arm, or back?',
      options: ['Yes', 'No']
    },

    highEnergyTrauma: {
      type: 'radio',
      label: 'Was this caused by a hard impact (e.g., car crash, bad fall)?',
      options: ['Yes', 'No']
    },

    feverChills: {
      type: 'radio',
      label: 'Do you have a fever or chills?',
      options: ['Yes', 'No']
    },

    unexplainedWeightLoss: {
      type: 'radio',
      label: 'Have you lost weight without trying?',
      options: ['Yes', 'No']
    },

    historyOfCancer: {
      type: 'radio',
      label: 'Have you ever been told you have cancer?',
      options: ['Yes', 'No']
    },

    anticoagulants: {
      type: 'radio',
      label: 'Do you take blood-thinning medication (anticoagulants)?',
      options: ['Yes', 'No']
    },

    osteoporosis: {
      type: 'radio',
      label: 'Have you been told you have osteoporosis (weak bones)?',
      options: ['Yes', 'No']
    },

    // RED-S / Bone Health
    menstrualStatus: {
      type: 'radio',
      label: 'If you have periods, how are they?',
      options: ['Regular', 'Irregular', 'No periods for 3+ months', 'Not applicable', 'Prefer not to say']
    },

    dietaryConcerns: {
      type: 'radio',
      label: 'Do you have any concerns about your diet or eating?',
      options: ['Yes', 'No', 'Prefer not to say']
    },

    previousStressFracture: {
      type: 'radio',
      label: 'Have you ever had a stress fracture?',
      options: ['Yes', 'No']
    },

    // Treatment
    medications: {
      type: 'textarea',
      label: 'What medications or painkillers have you tried for this problem?',
      placeholder: 'List any medications you have taken',
      rows: 2
    },

    imaging: {
      type: 'radio',
      label: 'Have you had any imaging (X-ray, CT, MRI, ultrasound)?',
      options: ['Yes', 'No']
    },

    imagingType: {
      type: 'text',
      label: 'What type of imaging did you have?',
      placeholder: 'e.g., X-ray, CT scan, MRI',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhere: {
      type: 'text',
      label: 'Where was the imaging done?',
      placeholder: 'e.g., Waikato Hospital, Pacific Radiology',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhen: {
      type: 'text',
      label: 'When was the imaging done?',
      placeholder: 'e.g., last week, 2 months ago',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingResults: {
      type: 'textarea',
      label: 'What were the results? (if you know)',
      placeholder: 'e.g., normal, rib fracture, no fracture seen',
      rows: 2,
      showWhen: { field: 'imaging', includes: 'Yes' }
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. About Your Chest / Rib Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'ribLevel', 'onsetType', 'mechanism', 'mechanismOther', 'injuryDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painCharacter', 'painCharacterOther', 'painSeverity', 'painWithBreathing', 'painWithCough', 'painWithRotation', 'painAtNight', 'painWithLyingOnSide', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'sport',
      title: '4. Your Sport & Training',
      component: 'GenericSection',
      enabled: true,
      fields: ['sportSpecific', 'recentTrainingChange']
    },
    {
      id: 'redFlags',
      title: '5. Important Safety Questions',
      component: 'RedFlagsSection',
      enabled: true,
      fields: [
        { id: 'shortnessOfBreath', label: 'Shortness of breath', weight: 8, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'coughingBlood', label: 'Coughing up blood', weight: 10, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'severeChestPain', label: 'Severe chest pain that does not go away', weight: 10, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'cardiacRadiation', label: 'Pain spreading to jaw, arm, or back', weight: 8, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'highEnergyTrauma', label: 'High-energy impact (e.g., car crash, bad fall)', weight: 6, type: 'radio', options: ['Yes', 'No'] },
        { id: 'feverChills', label: 'Fever or chills', weight: 4, type: 'radio', options: ['Yes', 'No'] },
        { id: 'unexplainedWeightLoss', label: 'Unexplained weight loss', weight: 5, type: 'radio', options: ['Yes', 'No'] },
        { id: 'historyOfCancer', label: 'History of cancer', weight: 5, type: 'radio', options: ['Yes', 'No'] },
        { id: 'anticoagulants', label: 'Taking blood-thinning medication', weight: 3, type: 'radio', options: ['Yes', 'No'] },
        { id: 'osteoporosis', label: 'Osteoporosis (weak bones)', weight: 2, type: 'radio', options: ['Yes', 'No'] }
      ],
      alerts: {
        critical: { threshold: 10, message: 'URGENT: These symptoms need immediate medical attention' },
        urgent: { threshold: 7, message: 'Please contact us promptly about these symptoms' },
        concerning: { threshold: 5, message: 'Please mention these symptoms at your appointment' }
      }
    },
    {
      id: 'bonehealth',
      title: '6. Bone Health (RED-S Screening)',
      component: 'GenericSection',
      enabled: true,
      fields: ['menstrualStatus', 'dietaryConcerns', 'previousStressFracture']
    },
    {
      id: 'treatment',
      title: '7. Treatment So Far',
      component: 'GenericSection',
      enabled: true,
      fields: ['medications', 'imaging', 'imagingType', 'imagingWhere', 'imagingWhen', 'imagingResults']
    },
    {
      id: 'medical',
      title: '8. Your Health',
      component: 'MedicalHistorySection',
      enabled: true
    },
    {
      id: 'exerciseVitalSign',
      title: '9. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },
    {
      id: 'goals',
      title: '10. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Gradually': 'gradual onset',
      'After coughing': 'onset following prolonged or forceful coughing'
    },
    commonDiagnoses: [
      'Rib fracture',
      'Rib stress fracture',
      'Costochondritis',
      'Intercostal muscle strain',
      'Rib contusion',
      'Tietze syndrome',
      'Sternoclavicular joint sprain',
      'Thoracic spine referred pain',
      'Slipping rib syndrome',
      'Stress fracture of rib (rowing / cough)',
      'Rib stress reaction',
      'Pneumothorax (requires exclusion)'
    ]
  }
};

export default ribTemplate;
